<template>
  <div class="signature-page">
    <!-- 主内容区域 -->
    <div class="signature-container">
      <!-- 标题 -->
      <div class="header">
        <h2 class="title">
          完善个人签名
        </h2>
        <p class="subtitle">
          签名会展示在备案证书，请认真书写
        </p>
      </div>

      <!-- 签名区域 -->
      <div class="signature-section">
        <div class="signature-canvas-wrapper">
          <canvas
            ref="signatureCanvas"
            class="signature-canvas"
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
            @mousedown="handleMouseDown"
            @mousemove="handleMouseMove"
            @mouseup="handleMouseUp"
            @mouseleave="handleMouseUp"
          />
          <div
            v-if="!hasSignature"
            class="signature-placeholder"
          >
            <p>请在此处签名</p>
          </div>
        </div>
        <div class="signature-actions">
          <el-button
            class="clear-btn"
            @click="clearSignature"
          >
            清除重写
          </el-button>
          <el-button
            type="primary"
            class="submit-btn"
            :disabled="!hasSignature"
            @click="submitSignature"
          >
            确认提交
          </el-button>
        </div>
      </div>

      <!-- 提示信息 -->
      <div class="tips">
        <p class="tip-text">
          请使用手指或触控笔在签名区域书写您的签名
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElLoading } from 'element-plus'
import { uploadFile } from '@/api/utils/file'
import { setToken } from '@/utils/auth'
import { upSignature } from '@/api/originalWorkFiling'

export default {
  name: 'Signature',
  data() {
    return {
      isDrawing: false,
      hasSignature: false,
      canvas: null,
      ctx: null,
      lastX: 0,
      lastY: 0,
      points: [], // 存储绘制点，用于平滑处理
      workId: null // 作品ID
    }
  },
  mounted() {
    // 等待DOM渲染完成后再初始化画布
    this.$nextTick(() => {
      setTimeout(() => {
        this.initCanvas()
      }, 100)
    })
    // 从URL参数获取workId
    this.workId = this.$route.query.workId
    if (this.workId) {
      console.log('作品ID:', this.workId)
    } else {
      ElMessage.error('未获取到作品ID（workId），请从业务页面重新进入签名页/重新扫码')
    }

    // 从URL参数获取token，用于扫码后识别并自动登录用户
    const token = this.$route.query.token
    if (token) {
      // 将二维码中的 token 写入本地认证存储，axios 拦截器会自动带 Authorization: Bearer <token>
      setToken(token)
      console.log('通过二维码设置用户 token')
    }
  },
  methods: {
    /**
     * 初始化画布
     */
    initCanvas() {
      this.canvas = this.$refs.signatureCanvas
      if (!this.canvas) return

      this.ctx = this.canvas.getContext('2d')
      
      // 设置画布尺寸
      const rect = this.canvas.getBoundingClientRect()
      const dpr = window.devicePixelRatio || 1
      
      this.canvas.width = rect.width * dpr
      this.canvas.height = rect.height * dpr
      
      this.ctx.scale(dpr, dpr)
      this.canvas.style.width = rect.width + 'px'
      this.canvas.style.height = rect.height + 'px'
      
      // 设置画笔样式 - 加粗手写字体效果
      this.ctx.strokeStyle = '#000000'
      this.ctx.fillStyle = '#000000' // 设置填充颜色，用于绘制圆点
      this.ctx.lineWidth = 5 // 增加线条宽度，让签名更粗更明显
      this.ctx.lineCap = 'round' // 圆形端点，更自然
      this.ctx.lineJoin = 'round' // 圆形连接，更流畅
      this.ctx.globalCompositeOperation = 'source-over' // 确保绘制模式正确
      this.ctx.shadowBlur = 0 // 禁用阴影，避免影响性能
    },
    /**
     * 获取触摸点坐标
     */
    getTouchPos(e) {
      const rect = this.canvas.getBoundingClientRect()
      const touch = e.touches ? e.touches[0] : e.changedTouches[0]
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      }
    },
    /**
     * 获取鼠标坐标
     */
    getMousePos(e) {
      const rect = this.canvas.getBoundingClientRect()
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      }
    },
    /**
     * 开始绘制
     */
    startDraw(x, y) {
      this.isDrawing = true
      this.lastX = x
      this.lastY = y
      this.points = [{ x, y }] // 初始化点数组
      
      // 在起始点绘制一个圆点，让签名更自然
      if (this.ctx) {
        this.ctx.beginPath()
        this.ctx.arc(x, y, this.ctx.lineWidth / 2, 0, Math.PI * 2)
        this.ctx.fill()
      }
    },
    /**
     * 绘制
     */
    draw(x, y) {
      if (!this.isDrawing || !this.ctx) return

      // 添加当前点到数组
      this.points.push({ x, y })
      
      // 如果点数少于2，直接绘制圆点
      if (this.points.length < 2) {
        this.ctx.beginPath()
        this.ctx.arc(x, y, this.ctx.lineWidth / 2, 0, Math.PI * 2)
        this.ctx.fill()
        this.lastX = x
        this.lastY = y
        this.hasSignature = true
        return
      }

      // 使用平滑的曲线绘制，让签名更连贯流畅
      const currentPoint = this.points[this.points.length - 1]
      const previousPoint = this.points[this.points.length - 2]
      
      // 计算控制点（使用上一个点和当前点的中点）
      const controlX = (previousPoint.x + currentPoint.x) / 2
      const controlY = (previousPoint.y + currentPoint.y) / 2
      
      // 使用二次贝塞尔曲线绘制平滑线条
      // 控制点使用上一个点，终点使用中点，这样更连贯
      this.ctx.beginPath()
      this.ctx.moveTo(this.lastX, this.lastY)
      this.ctx.quadraticCurveTo(previousPoint.x, previousPoint.y, controlX, controlY)
      this.ctx.stroke()
      
      // 在控制点绘制小圆点，增强粗体效果，半径较小不影响连贯性
      this.ctx.beginPath()
      this.ctx.arc(controlX, controlY, this.ctx.lineWidth / 4, 0, Math.PI * 2)
      this.ctx.fill()

      this.lastX = controlX
      this.lastY = controlY
      this.hasSignature = true
      
      // 保持数组大小，避免内存溢出（只保留最近4个点用于平滑）
      if (this.points.length > 4) {
        this.points.shift()
      }
    },
    /**
     * 结束绘制
     */
    endDraw() {
      if (this.isDrawing && this.ctx && this.points.length > 0) {
        // 绘制最后一段，确保线条完整连贯
        const lastPoint = this.points[this.points.length - 1]
        const distance = Math.sqrt(
          Math.pow(this.lastX - lastPoint.x, 2) + Math.pow(this.lastY - lastPoint.y, 2)
        )
        
        if (distance > 1) {
          // 如果距离较大，使用直线连接
          this.ctx.beginPath()
          this.ctx.moveTo(this.lastX, this.lastY)
          this.ctx.lineTo(lastPoint.x, lastPoint.y)
          this.ctx.stroke()
          
          // 在终点绘制小圆点
          this.ctx.beginPath()
          this.ctx.arc(lastPoint.x, lastPoint.y, this.ctx.lineWidth / 3, 0, Math.PI * 2)
          this.ctx.fill()
        }
      }
      this.isDrawing = false
      this.points = []
    },
    /**
     * 触摸开始
     */
    handleTouchStart(e) {
      e.preventDefault()
      const pos = this.getTouchPos(e)
      this.startDraw(pos.x, pos.y)
    },
    /**
     * 触摸移动
     */
    handleTouchMove(e) {
      e.preventDefault()
      const pos = this.getTouchPos(e)
      this.draw(pos.x, pos.y)
    },
    /**
     * 触摸结束
     */
    handleTouchEnd(e) {
      e.preventDefault()
      this.endDraw()
    },
    /**
     * 鼠标按下
     */
    handleMouseDown(e) {
      const pos = this.getMousePos(e)
      this.startDraw(pos.x, pos.y)
    },
    /**
     * 鼠标移动
     */
    handleMouseMove(e) {
      const pos = this.getMousePos(e)
      this.draw(pos.x, pos.y)
    },
    /**
     * 鼠标抬起
     */
    handleMouseUp() {
      this.endDraw()
    },
    /**
     * 清除签名
     */
    clearSignature() {
      if (!this.ctx || !this.canvas) return
      
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      this.hasSignature = false
    },
    /**
     * 下载签名图片
     */
    downloadSignature(dataUrl) {
      try {
        // 创建下载链接
        const link = document.createElement('a')
        link.href = dataUrl
        link.download = `签名_${new Date().getTime()}.png`
        
        // 触发下载
        document.body.appendChild(link)
        link.click()
        
        // 清理
        document.body.removeChild(link)
      } catch (error) {
        console.error('下载签名失败:', error)
        ElMessage.error('下载签名失败')
      }
    },
    /**
     * 将data URL转换为File对象
     */
    dataURLtoFile(dataUrl, filename) {
      const arr = dataUrl.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, { type: mime })
    },
    /**
     * 上传签名图片
     */
    async uploadSignatureImage(signatureDataUrl) {
      try {
        // 将data URL转换为File对象
        const file = this.dataURLtoFile(signatureDataUrl, `signature_${Date.now()}.png`)
        
        // 创建FormData
        const formData = new FormData()
        formData.append('file', file)
        
        // 显示上传加载状态
        const loading = ElLoading.service({
          lock: true,
          text: '签名上传中...',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        
        // 调用上传接口
        const response = await uploadFile(formData)
        loading.close()
        
        if (response && response.code === 200) {
          const signatureUrl = response.data?.originalUrl
          if (signatureUrl) {
            return signatureUrl
          } else {
            throw new Error('上传失败，未获取到文件地址')
          }
        } else {
          throw new Error(response.msg || '上传失败')
        }
      } catch (error) {
        console.error('上传签名图片失败:', error)
        throw error
      }
    },
    /**
     * 提交签名
     */
    async submitSignature() {
      if (!this.hasSignature) {
        ElMessage.warning('请先完成签名')
        return
      }
      if (!this.workId) {
        ElMessage.error('缺少作品ID（workId），无法提交签名')
        return
      }

      try {
        // 将画布转换为图片
        const signatureDataUrl = this.canvas.toDataURL('image/png')
        
        // 上传签名图片并获取文件URL
        const signatureUrl = await this.uploadSignatureImage(signatureDataUrl)
        
        // 将签名文件URL存储到localStorage，使用workId作为key
        if (this.workId) {
          const storageKey = `signature_${this.workId}`
          localStorage.setItem(storageKey, signatureUrl)
          console.log('签名文件URL已保存:', signatureUrl)
          // 触发自定义事件，通知父页面签名已完成
          window.dispatchEvent(new CustomEvent('signature-completed', {
            detail: { workId: this.workId, signatureUrl }
          }))
        }
        this.formData = {
          id : this.workId,
          signatureQrCodeUrl: signatureUrl
        }
        const response = await upSignature(this.formData)
          if (response && response.code === 200 && response.data) {
            ElMessage.success('签名提交成功')
        }
        
        // 延迟后返回或关闭页面
        setTimeout(() => {
          // 如果是微信环境，可以关闭当前窗口
          if (window.WeixinJSBridge) {
            WeixinJSBridge.call('closeWindow')
          } else {
            // 否则返回上一页或关闭
            window.history.back()
          }
        }, 1500)
      } catch (error) {
        console.error('提交签名失败:', error)
        ElMessage.error(error.message || '提交签名失败，请重试')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/* 签名页仅支持手机端与平板，无电脑端 */

.signature-page {
  min-height: auto;
  background: linear-gradient(180deg, #f5f7fa 0%, #fff 50%);
  box-sizing: border-box;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.signature-container {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 20px 16px 24px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.header {
  text-align: center;
  margin-bottom: 20px;
  flex-shrink: 0;

  .title {
    font-size: 22px;
    font-weight: 600;
    color: #191919;
    margin: 0 0 6px 0;
    line-height: 1.3;
  }

  .subtitle {
    font-size: 13px;
    color: #696969;
    margin: 0;
    line-height: 1.5;
  }
}

.signature-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 58px;
}

.signature-canvas-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  flex-shrink: 0;

  .signature-canvas {
    display: block;
    width: 100%;
    height: 100%;
    touch-action: none;
    cursor: crosshair;
  }

  .signature-placeholder {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    color: #c0c4cc;
    font-size: 15px;
  }
}

.signature-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;

  .clear-btn {
    flex: 1;
    height: 46px;
    border-radius: 8px;
    border-color: #dcdfe6;
    color: #606266;
    font-size: 15px;
    font-weight: 500;

    &:hover {
      border-color: #c0c4cc;
      color: #909399;
    }
  }

  .submit-btn {
    flex: 1;
    height: 46px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    background-color: #0d63f3;
    border-color: #0d63f3;

    &:hover:not(:disabled) {
      background-color: #0b56d9;
      border-color: #0b56d9;
    }

    &:disabled {
      background-color: #c0c4cc;
      border-color: #c0c4cc;
      opacity: 0.6;
    }
  }
}

.tips {
  text-align: center;
  padding-top: 16px;
  margin-top: 4px;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;

  .tip-text {
    font-size: 12px;
    color: #909399;
    margin: 0;
    line-height: 1.5;
  }
}

/* 平板适配 */
@media (min-width: 769px) {
  .signature-container {
    max-width: 480px;
    padding: 24px 20px 28px;
  }

  .header {
    margin-bottom: 24px;

    .title {
      font-size: 24px;
    }

    .subtitle {
      font-size: 14px;
    }
  }

  .signature-section {
    gap: 20px;
    margin-top: 58px;
  }

  .signature-actions {
    gap: 12px;

    .clear-btn,
    .submit-btn {
      height: 48px;
      font-size: 16px;
    }
  }

  .tips {
    padding-top: 20px;
    margin-top: 8px;

    .tip-text {
      font-size: 13px;
    }
  }
}
</style>

